import { Component, ElementRef, EventEmitter, HostListener, OnInit, Output, ViewChild } from '@angular/core';
import { ChartType } from 'angular-google-charts';
import moment from 'moment';
import { DiagnosticGuideService } from 'src/app/services/consultation/diagnostic/diagnostic-guide.service';
import { HistoriqueCritere } from 'src/app/utils/interfaces/new/historique-critere.interface';
import { CritereHistoriqueItem } from 'src/app/utils/interfaces/new/critere-historique-item.interface';
import { environment } from 'src/environments/environment';

type HistoriqueFilter = 'last10' | 'last20' | 'all' | 'period';

@Component({
  selector: 'app-consult-data',
  templateUrl: './consult-data.fragment.html',
  styleUrls: [
    './consult-data.fragment.scss',
    '../modal.fragment.scss'
  ]
})
export class ConsultDataFragment implements OnInit {

  @ViewChild('modalBody') public modalBody: ElementRef;
  @ViewChild('outsideModal') public outsideModal: ElementRef;

  patientId: string;
  criteres: CritereHistoriqueItem[] = [];
  filteredCriteres: CritereHistoriqueItem[] = [];
  selectedCritere: CritereHistoriqueItem;
  historique: HistoriqueCritere[] = [];

  searchTerm = '';
  filterMode: HistoriqueFilter = 'last10';
  dateDebut = '';
  dateFin = '';
  periodError = '';

  loading = false;
  loadError = '';
  private loadVersion = 0;

  chart: {
    title: string;
    type: ChartType;
    data: any[];
    options: any;
  } = {
    title: '',
    type: ChartType.Line,
    data: [],
    options: {}
  };

  @Output() public closeEmitter = new EventEmitter();

  constructor(private _guide: DiagnosticGuideService) {}

  async ngOnInit(): Promise<void> {
    this.criteres = this.deduplicateCriteres(this.criteres || []);
    this.filteredCriteres = [...this.criteres];

    if (this.criteres.length > 0) {
      this.selectedCritere = this.criteres[0];
      await this.loadHistorique();
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent): void {
    if (!environment.closeModalOnClickOutside || !this.modalBody || !this.outsideModal) {
      return;
    }

    if (!this.modalBody.nativeElement.contains(event.target) && this.outsideModal.nativeElement.contains(event.target)) {
      this.closeAddEditModal();
    }
  }

  private deduplicateCriteres(criteres: CritereHistoriqueItem[]): CritereHistoriqueItem[] {
    const map = new Map<string, CritereHistoriqueItem>();
    criteres.forEach(critere => {
      if (critere && critere.idCritere && !map.has(critere.idCritere)) {
        map.set(critere.idCritere, critere);
      }
    });
    return Array.from(map.values());
  }

  public closeAddEditModal(object?: any): void {
    this.closeEmitter.emit(object || null);
  }

  public onSearchCritere(event: Event): void {
    const value = (event.target as HTMLInputElement).value || '';
    this.searchTerm = value;
    const search = value.trim().toLocaleUpperCase();

    this.filteredCriteres = !search
      ? [...this.criteres]
      : this.criteres.filter(x =>
          (x.nomCritere || '').toLocaleUpperCase().includes(search)
        );
  }

  public clearSearch(): void {
    this.searchTerm = '';
    this.filteredCriteres = [...this.criteres];
  }

  public async selectCritere(critere: CritereHistoriqueItem): Promise<void> {
    if (!critere || this.selectedCritere?.idCritere === critere.idCritere) {
      return;
    }

    this.selectedCritere = critere;

    if (this.filterMode === 'period' && (!this.dateDebut || !this.dateFin)) {
      this.historique = [];
      this.chart.data = [];
      return;
    }

    await this.loadHistorique();
  }

  public async setFilter(mode: HistoriqueFilter): Promise<void> {
    if (this.filterMode === mode) {
      return;
    }

    this.filterMode = mode;
    this.periodError = '';

    if (mode === 'period') {
      return;
    }

    await this.loadHistorique();
  }

  public async applyPeriod(): Promise<void> {
    this.periodError = '';

    if (!this.dateDebut || !this.dateFin) {
      this.periodError = 'Veuillez renseigner une date de début et une date de fin.';
      return;
    }

    if (this.dateDebut > this.dateFin) {
      this.periodError = 'La date de début doit être antérieure ou égale à la date de fin.';
      return;
    }

    await this.loadHistorique();
  }

  public async loadHistorique(): Promise<void> {
    if (!this.patientId || !this.selectedCritere?.idCritere) {
      return;
    }

    const currentLoad = ++this.loadVersion;
    this.loading = true;
    this.loadError = '';
    this.historique = [];
    this.chart.data = [];

    try {
      let result: HistoriqueCritere[];

      switch (this.filterMode) {
        case 'last20':
          result = await this._guide.getHistoriqueCritere(
            this.patientId,
            this.selectedCritere.idCritere,
            { mode: 'last', limit: 20 }
          );
          break;

        case 'all':
          result = await this._guide.getHistoriqueCritere(
            this.patientId,
            this.selectedCritere.idCritere,
            { mode: 'all' }
          );
          break;

        case 'period':
          result = await this._guide.getHistoriqueCritere(
            this.patientId,
            this.selectedCritere.idCritere,
            {
              mode: 'period',
              dateDebut: this.dateDebut,
              dateFin: this.dateFin
            }
          );
          break;

        case 'last10':
        default:
          result = await this._guide.getHistoriqueCritere(
            this.patientId,
            this.selectedCritere.idCritere,
            { mode: 'last', limit: 10 }
          );
          break;
      }

      if (currentLoad !== this.loadVersion) {
        return;
      }

      this.historique = (result || []).slice().sort((a, b) =>
        moment(a.date).valueOf() - moment(b.date).valueOf()
      );

      this.buildChart();
    } catch (error) {
      if (currentLoad !== this.loadVersion) {
        return;
      }

      this.loadError = 'Impossible de charger l’historique de ce critère.';
      this.historique = [];
      this.chart.data = [];
    } finally {
      if (currentLoad === this.loadVersion) {
        this.loading = false;
      }
    }
  }

  private buildChart(): void {
    this.chart.title = this.selectedCritere?.nomCritere || '';
    this.chart.data = [];

    if (!this.isNumericCritere()) {
      return;
    }

    const values: number[] = [];

    this.historique.forEach(ligne => {
      const value = this.parseNumericValue(ligne.valeur);
      if (value !== null) {
        values.push(value);
        this.chart.data.push([moment(ligne.date).toDate(), value]);
      }
    });

    if (!values.length) {
      return;
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const margin = range > 0
      ? Math.max(range * 0.12, Math.abs(max) * 0.01, 0.01)
      : Math.max(Math.abs(max) * 0.03, 0.01);

    this.chart.options = {
      legend: { position: 'none' },
      lineWidth: 3,
      pointSize: 6,
      chartArea: {
        left: 60,
        right: 25,
        top: 25,
        bottom: 55,
        width: '88%',
        height: '72%'
      },
      hAxis: {
        format: 'dd/MM/yyyy',
        textStyle: { fontSize: 12 }
      },
      vAxis: {
        title: this.selectedCritere?.uniteCritere || '',
        viewWindow: {
          min: min - margin,
          max: max + margin
        },
        textStyle: { fontSize: 12 }
      }
    };
  }

  private parseNumericValue(value: string): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value)
      .trim()
      .replace(/\s/g, '')
      .replace(',', '.');

    if (!normalized) {
      return null;
    }

    const numericValue = Number(normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  public isNumericCritere(): boolean {
    return this.isNumericItem(this.selectedCritere);
  }

  public isNumericItem(critere: CritereHistoriqueItem): boolean {
    const type = (critere?.typeCritere || '').toLocaleLowerCase().trim();
    return type === 'numerique' || type === 'numérique';
  }

  public showTimeline(): boolean {
    if (!this.selectedCritere || this.isNumericCritere()) {
      return false;
    }

    return !!this.selectedCritere.aideSaisie;
  }

  public showTextOnly(): boolean {
    return !!this.selectedCritere && !this.isNumericCritere() && !this.showTimeline();
  }

  public get historiqueTable(): HistoriqueCritere[] {
    return [...this.historique].reverse();
  }

  public formatDate(date: string | Date): string {
    return moment(date).format('DD/MM/YYYY');
  }

  public getFilterLabel(): string {
    switch (this.filterMode) {
      case 'last20':
        return '20 derniers résultats';
      case 'all':
        return 'Tout l’historique';
      case 'period':
        return this.dateDebut && this.dateFin
          ? `Du ${moment(this.dateDebut).format('DD/MM/YYYY')} au ${moment(this.dateFin).format('DD/MM/YYYY')}`
          : 'Période personnalisée';
      case 'last10':
      default:
        return '10 derniers résultats';
    }
  }

  public trackByCritere(index: number, item: CritereHistoriqueItem): string {
    return item.idCritere;
  }

  public trackByHistorique(index: number, item: HistoriqueCritere): string {
    return `${item.consultationId}-${item.diagnosticId}-${index}`;
  }

  private escapeHtml(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  public print(): void {
    if (!this.selectedCritere || !this.historique.length) return;

    const visualisation = this.isNumericCritere()
      ? this.buildPrintChart()
      : this.showTimeline()
        ? this.buildPrintTimeline()
        : '';

    const html = this.buildPrintDocument(
      visualisation,
      this.buildPrintTable()
    );

    this.printDocument(html);
  }

  private buildPrintChart(): string {
    const values = this.historique
      .map(ligne => ({
        date: new Date(ligne.date).getTime(),
        value: this.parseNumericValue(ligne.valeur)
      }))
      .filter(item => item.value !== null && !isNaN(item.date)) as Array<{ date:number; value:number }>;

    if (!values.length) return '';

    const width = 760;
    const height = 280;
    const left = 58;
    const right = 18;
    const top = 22;
    const bottom = 46;
    const graphWidth = width - left - right;
    const graphHeight = height - top - bottom;

    const rawMin = Math.min(...values.map(item => item.value));
    const rawMax = Math.max(...values.map(item => item.value));
    const rawRange = rawMax - rawMin;

    const margin = rawRange > 0
      ? Math.max(rawRange * 0.12, Math.abs(rawMax) * 0.01, 0.01)
      : Math.max(Math.abs(rawMax) * 0.05, 1);

    const minValue = rawMin - margin;
    const maxValue = rawMax + margin;
    const range = maxValue - minValue || 1;

    const minDate = Math.min(...values.map(item => item.date));
    const maxDate = Math.max(...values.map(item => item.date));
    const dateRange = maxDate - minDate;

    const getX = (date:number, index:number):number => {
      if (dateRange === 0) {
        if (values.length === 1) return left + graphWidth / 2;
        return left + (index / (values.length - 1)) * graphWidth;
      }
      return left + ((date - minDate) / dateRange) * graphWidth;
    };

    const getY = (value:number):number =>
      top + graphHeight - ((value - minValue) / range) * graphHeight;

    const points = values
      .map((item, index) => `${getX(item.date, index).toFixed(2)},${getY(item.value).toFixed(2)}`)
      .join(' ');

    const yTicks = 5;
    let grid = '';

    for (let i = 0; i < yTicks; i++) {
      const ratio = i / (yTicks - 1);
      const y = top + graphHeight - ratio * graphHeight;
      const value = minValue + ratio * range;

      grid += `
        <line x1="${left}" y1="${y}" x2="${width - right}" y2="${y}" stroke="#e5e9ed" stroke-width="1"/>
        <text x="${left - 8}" y="${y + 4}" text-anchor="end" font-size="10" fill="#687481">
          ${this.escapeHtml(this.formatPrintNumber(value))}
        </text>
      `;
    }

    const labelIndexes:number[] = [];
    const maxLabels = Math.min(5, values.length);

    if (maxLabels === 1) {
      labelIndexes.push(0);
    } else {
      for (let i = 0; i < maxLabels; i++) {
        const index = Math.round((i * (values.length - 1)) / (maxLabels - 1));
        if (labelIndexes.indexOf(index) === -1) labelIndexes.push(index);
      }
    }

    const xLabels = labelIndexes.map(index => {
      const item = values[index];
      const x = getX(item.date, index);

      return `
        <line x1="${x}" y1="${top + graphHeight}" x2="${x}" y2="${top + graphHeight + 5}" stroke="#9aa4ae" stroke-width="1"/>
        <text x="${x}" y="${height - 15}" text-anchor="middle" font-size="9" fill="#687481">
          ${moment(item.date).format('DD/MM/YY')}
        </text>
      `;
    }).join('');

    const circles = values.map((item, index) => `
      <circle
        cx="${getX(item.date, index)}"
        cy="${getY(item.value)}"
        r="4"
        fill="#0D436F"
        stroke="#ffffff"
        stroke-width="1.5"/>
    `).join('');

    const unit = this.selectedCritere.uniteCritere
      ? `<text x="${left}" y="12" font-size="9" font-weight="600" fill="#687481">${this.escapeHtml(this.selectedCritere.uniteCritere)}</text>`
      : '';

    return `
      <div class="print-chart">
        <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="xMidYMid meet" role="img">
          ${unit}
          ${grid}

          <line x1="${left}" y1="${top}" x2="${left}" y2="${top + graphHeight}" stroke="#9aa4ae" stroke-width="1"/>
          <line x1="${left}" y1="${top + graphHeight}" x2="${width - right}" y2="${top + graphHeight}" stroke="#9aa4ae" stroke-width="1"/>

          ${xLabels}

          ${values.length > 1
            ? `<polyline points="${points}" fill="none" stroke="#0D436F" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>`
            : ''}

          ${circles}
        </svg>
      </div>
    `;
  }

  private buildPrintTimeline(): string {
    const itemsPerRow = 5;
    const rows:string[] = [];

    for (let i = 0; i < this.historique.length; i += itemsPerRow) {
      const chunk = this.historique.slice(i, i + itemsPerRow);

      const items = chunk.map((ligne, index) => `
        <div class="print-timeline-item">
          <div class="print-timeline-date">
            ${this.escapeHtml(this.formatDate(ligne.date))}
          </div>

          <div class="print-timeline-dot"></div>

          <div class="print-timeline-value">
            ${this.escapeHtml(ligne.valeur)}
          </div>

          ${index < chunk.length - 1 ? '<div class="print-timeline-line"></div>' : ''}
        </div>
      `).join('');

      rows.push(`
        <div class="print-timeline-row">
          ${items}
        </div>
      `);
    }

    return `
      <div class="print-timeline">
        ${rows.join('')}
      </div>
    `;
  }

  private buildPrintTable(): string {
    const rows = this.historiqueTable.map(ligne => `
      <tr>
        <td>${this.escapeHtml(this.formatDate(ligne.date))}</td>
        <td>
          <strong>${this.escapeHtml(ligne.valeur)}</strong>
          ${this.selectedCritere.uniteCritere
            ? `<span class="print-unit">${this.escapeHtml(this.selectedCritere.uniteCritere)}</span>`
            : ''}
        </td>
      </tr>
    `).join('');

    return `
      <div class="print-card print-table-card">
        <div class="print-card-header">
          <div>
            <div class="print-card-title">Valeurs</div>
            <div class="print-card-subtitle">De la plus récente à la plus ancienne</div>
          </div>

          <div class="print-count">
            ${this.historique.length} résultat${this.historique.length > 1 ? 's' : ''}
          </div>
        </div>

        <table class="print-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Valeur</th>
            </tr>
          </thead>

          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>
    `;
  }

  private buildPrintDocument(visualisation:string, table:string): string {
    const critere = this.escapeHtml(this.selectedCritere.nomCritere);
    const unite = this.selectedCritere.uniteCritere
      ? `<span class="print-unit-badge">${this.escapeHtml(this.selectedCritere.uniteCritere)}</span>`
      : '';

    return `
  <!DOCTYPE html>
  <html>
  <head>
  <meta charset="utf-8">
  <title>Historique - ${critere}</title>

  <style>
  @page {
    size:A4 portrait;
    margin:12mm 12mm 14mm 12mm;
  }

  html,body {
    margin:0;
    padding:0;
    background:#ffffff;
    color:#283544;
    font-family:Arial,Helvetica,sans-serif;
    font-size:11px;
  }

  * {
    box-sizing:border-box;
    -webkit-print-color-adjust:exact;
    print-color-adjust:exact;
  }

  .print-page {
    width:100%;
  }

  .print-document-title {
    margin:0 0 14px 0;
    padding:0 0 9px 0;
    border-bottom:2px solid #0D436F;
    color:#0D436F;
    font-size:20px;
    font-weight:700;
  }

  /* Futur emplacement du cartouche patient / INS */

  .print-header {
    width:100%;
    margin-bottom:14px;
    display:flex;
    align-items:flex-end;
    justify-content:space-between;
    gap:20px;
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .print-label {
    margin-bottom:3px;
    color:#788491;
    font-size:9px;
    font-weight:700;
    letter-spacing:.5px;
    text-transform:uppercase;
  }

  .print-critere {
    color:#253342;
    font-size:18px;
    font-weight:700;
  }

  .print-unit-badge {
    display:inline-block;
    margin-left:6px;
    padding:2px 6px;
    border-radius:4px;
    background:#eef2f5;
    color:#697582;
    font-size:10px;
    font-weight:600;
    vertical-align:middle;
  }

  .print-selection {
    color:#65717e;
    font-size:11px;
    text-align:right;
  }

  .print-card {
    width:100%;
    margin-bottom:12px;
    border:1px solid #dce3e9;
    border-radius:7px;
    background:#ffffff;
    overflow:hidden;
  }

  .print-visual-card {
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .print-card-header {
    min-height:42px;
    padding:8px 11px;
    border-bottom:1px solid #e8edf1;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:10px;
  }

  .print-card-title {
    color:#354250;
    font-size:13px;
    font-weight:700;
  }

  .print-card-subtitle {
    margin-top:2px;
    color:#86919c;
    font-size:9px;
  }

  .print-count {
    flex:0 0 auto;
    padding:3px 7px;
    border-radius:10px;
    background:#f0f3f6;
    color:#687481;
    font-size:9px;
    font-weight:700;
  }

  .print-chart {
    width:100%;
    height:72mm;
    padding:3mm 4mm 2mm;
  }

  .print-chart svg {
    display:block;
    width:100%;
    height:100%;
  }

  .print-table-card {
    break-inside:auto;
    page-break-inside:auto;
  }

  .print-table {
    width:100%;
    border-collapse:collapse;
    table-layout:fixed;
  }

  .print-table thead {
    display:table-header-group;
  }

  .print-table th {
    padding:7px 10px;
    border-bottom:1px solid #dce3e9;
    background:#f5f7f9;
    color:#65717e;
    text-align:left;
    font-size:9px;
    font-weight:700;
    text-transform:uppercase;
  }

  .print-table th:first-child {
    width:34%;
  }

  .print-table td {
    padding:7px 10px;
    border-bottom:1px solid #edf0f3;
    color:#45515e;
    font-size:11px;
    vertical-align:top;
  }

  .print-table tr {
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .print-table tbody tr:last-child td {
    border-bottom:0;
  }

  .print-unit {
    margin-left:4px;
    color:#87929d;
    font-size:9px;
    font-weight:400;
  }

  .print-timeline {
    width:100%;
    padding:18px 12px;
  }

  .print-timeline-row {
    width:100%;
    margin-bottom:18px;
    display:flex;
    align-items:flex-start;
    break-inside:avoid;
    page-break-inside:avoid;
  }

  .print-timeline-row:last-child {
    margin-bottom:0;
  }

  .print-timeline-item {
    flex:1 1 0;
    min-width:0;
    position:relative;
    text-align:center;
  }

  .print-timeline-date {
    height:18px;
    color:#74808c;
    font-size:8px;
  }

  .print-timeline-dot {
    width:10px;
    height:10px;
    position:relative;
    z-index:2;
    margin:5px auto 9px auto;
    border-radius:50%;
    background:#0D436F;
    box-shadow:0 0 0 3px #ffffff,0 0 0 4px #bacbd8;
  }

  .print-timeline-line {
    position:absolute;
    z-index:1;
    top:28px;
    left:50%;
    width:100%;
    height:2px;
    background:#d5dfe7;
  }

  .print-timeline-value {
    margin:0 5px;
    padding:5px 6px;
    border-radius:5px;
    background:#f5f7f9;
    color:#3e4a56;
    font-size:9px;
    line-height:1.25;
    word-break:break-word;
  }

  .print-generated {
    margin-top:8px;
    color:#98a2ad;
    font-size:8px;
    text-align:right;
  }

  /* Futur emplacement du pied de page HyperMed */
  </style>
  </head>

  <body>
  <div class="print-page">

    <div class="print-document-title">
      Historique / suivi des critères
    </div>

    <!-- Futur cartouche patient / INS -->

    <div class="print-header">
      <div>
        <div class="print-label">Critère</div>

        <div class="print-critere">
          ${critere}
          ${unite}
        </div>
      </div>

      <div class="print-selection">
        <div class="print-label">Sélection</div>
        ${this.escapeHtml(this.getFilterLabel())}
      </div>
    </div>

    ${visualisation ? `
      <div class="print-card print-visual-card">
        <div class="print-card-header">
          <div>
            <div class="print-card-title">Évolution</div>
            <div class="print-card-subtitle">${this.escapeHtml(this.getFilterLabel())}</div>
          </div>

          <div class="print-count">
            ${this.historique.length} résultat${this.historique.length > 1 ? 's' : ''}
          </div>
        </div>

        ${visualisation}
      </div>
    ` : ''}

    ${table}

    <div class="print-generated">
      Document édité le ${moment().format('DD/MM/YYYY [à] HH:mm')}
    </div>

  </div>
  </body>
  </html>`;
  }

  private printDocument(html:string): void {
    const iframe = document.createElement('iframe');

    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';

    iframe.onload = () => {
      const printWindow = iframe.contentWindow;

      if (!printWindow) {
        iframe.remove();
        return;
      }

      const cleanup = () => {
        if (iframe.parentNode) iframe.remove();
      };

      printWindow.addEventListener('afterprint', cleanup);

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 150);

      setTimeout(cleanup, 60000);
    };

    iframe.srcdoc = html;
    document.body.appendChild(iframe);
  }

  private formatPrintNumber(value:number): string {
    if (Math.abs(value) >= 100) {
      return value.toFixed(0).replace('.', ',');
    }

    if (Math.abs(value) >= 10) {
      return value.toFixed(1).replace(/\.0$/, '').replace('.', ',');
    }

    return value.toFixed(2).replace(/0+$/, '').replace(/\,$/, '').replace('.', ',');
  }

}
